import { Fragment, useEffect, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStores } from "@/hooks/use-stores";
import { useActiveStore } from "@/hooks/use-active-store";

export function StoreSwitcher() {
  const { data: stores, isLoading } = useStores();
  const { storeId, setStoreId } = useActiveStore();

  // Veleprodaja je poseban način rada, pa uvijek ide na dno liste (odvojena
  // i naglašena) kako se ne bi pomiješala s maloprodajnim trgovinama.
  const sortedStores = useMemo(() => {
    if (!stores) return undefined;
    const retail = stores.filter((s: any) => s.storeType !== "wholesale");
    const wholesale = stores.filter((s: any) => s.storeType === "wholesale");
    return [...retail, ...wholesale];
  }, [stores]);

  // Auto-pick the first store on login (or after a stored store becomes invalid)
  // so the user never operates without a store selected.
  useEffect(() => {
    if (!sortedStores || sortedStores.length === 0) return;
    const validIds = new Set(sortedStores.map((s: any) => s.id));
    if (!storeId || !validIds.has(storeId)) {
      setStoreId(sortedStores[0].id);
    }
  }, [sortedStores, storeId, setStoreId]);

  if (isLoading) return <div className="px-2.5 py-1.5 text-xs text-muted-foreground">Učitavanje...</div>;

  if (!sortedStores || sortedStores.length === 0) {
    return <div className="px-2.5 py-1.5 text-xs text-muted-foreground">Nema trgovina</div>;
  }

  return (
    <Select value={storeId || ""} onValueChange={setStoreId}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Odaberite trgovinu" />
      </SelectTrigger>
      <SelectContent>
        {sortedStores.map((store: any, index: number) => {
          const isWholesale = store.storeType === "wholesale";
          const isFirstWholesale = isWholesale && sortedStores[index - 1]?.storeType !== "wholesale";
          return (
            <Fragment key={store.id}>
              {isFirstWholesale && index > 0 ? <SelectSeparator /> : null}
              <SelectItem
                value={store.id}
                className={isWholesale ? "font-semibold text-primary focus:text-primary" : undefined}
              >
                {store.name}
              </SelectItem>
            </Fragment>
          );
        })}
      </SelectContent>
    </Select>
  );
}
